/**
 * CR156: "Run until" configuration and display utilities
 * Maps ApprovalCheckpoint enum values to user-friendly labels and tooltips
 * 
 * Full 5-option model:
 * - Staging (S1-S3): Collection only
 * - Dedup (S1-S5): Through deduplication
 * - Review (S1-S6): Through review
 * - Publish (S1-S7): Through publish
 * - Cleanup (S1-S8): Full pipeline
 */

import type { ApprovalCheckpoint } from './types';

export interface RunUntilConfig {
  label: string;
  tooltipTitle: string;
  tooltipBody: string;
  steps: string;
  warningText?: string;
}

/**
 * Mapping from ApprovalCheckpoint enum to display configuration
 * 
 * Enum values:
 * - STOP_AFTER_COLLECT → "Staging" (S1-S3)
 * - STOP_AFTER_NORMALIZE_DEDUP → "Dedup" (S1-S5)
 * - STOP_AFTER_REVIEW → "Review" (S1-S6)
 * - AUTO_PUBLISH → "Publish" (S1-S7)
 * - AUTO_CLEANUP → "Cleanup" (S1-S8)
 */
export const RUN_UNTIL_CONFIG: Record<ApprovalCheckpoint, RunUntilConfig> = {
  STOP_AFTER_COLLECT: {
    label: 'Staging',
    tooltipTitle: 'Automation level',
    tooltipBody: 'Runs collection steps only (S1–S3). Remaining steps stay manual.',
    steps: 'S1–S3',
  },
  STOP_AFTER_NORMALIZE_DEDUP: {
    label: 'Dedup',
    tooltipTitle: 'Automation level',
    tooltipBody: 'Runs through Dedup (S1–S5). Review and Publish stay manual.',
    steps: 'S1–S5',
  },
  STOP_AFTER_REVIEW: {
    label: 'Review',
    tooltipTitle: 'Automation level',
    tooltipBody: 'Runs through Review (S1–S6). Publish stays manual.',
    steps: 'S1–S6',
  },
  AUTO_PUBLISH: {
    label: 'Publish',
    tooltipTitle: 'Automation level',
    tooltipBody: 'Runs through Publish (S1–S7). Cleanup stays manual.',
    steps: 'S1–S7',
    warningText: 'Publishing will run automatically when enabled. Use with caution.',
  },
  AUTO_CLEANUP: {
    label: 'Cleanup',
    tooltipTitle: 'Automation level',
    tooltipBody: 'Runs all steps (S1–S8).',
    steps: 'S1–S8',
    warningText: 'Runs the full pipeline automatically (including cleanup). Use with caution.',
  },
};

/**
 * Default checkpoint value for new schedules and legacy rows
 */
export const DEFAULT_CHECKPOINT: ApprovalCheckpoint = 'STOP_AFTER_NORMALIZE_DEDUP';

/**
 * Get display configuration for a checkpoint value
 * Falls back to default (Dedup) for null/undefined values
 */
export function getRunUntilConfig(checkpoint: ApprovalCheckpoint | null | undefined): RunUntilConfig {
  if (!checkpoint || !(checkpoint in RUN_UNTIL_CONFIG)) {
    return {
      ...RUN_UNTIL_CONFIG[DEFAULT_CHECKPOINT],
      tooltipBody: 'Default behavior. Runs through Dedup (S1–S5). Review and Publish stay manual.',
    };
  }
  return RUN_UNTIL_CONFIG[checkpoint];
}

/**
 * Get the display label for a checkpoint
 */
export function getRunUntilLabel(checkpoint: ApprovalCheckpoint | null | undefined): string {
  return getRunUntilConfig(checkpoint).label;
}

/**
 * All available checkpoint options for the editor dropdown
 * Ordered from least to most automated
 */
export const CHECKPOINT_OPTIONS: Array<{ value: ApprovalCheckpoint; label: string }> = [
  { value: 'STOP_AFTER_COLLECT', label: 'Staging' },
  { value: 'STOP_AFTER_NORMALIZE_DEDUP', label: 'Dedup' },
  { value: 'STOP_AFTER_REVIEW', label: 'Review' },
  { value: 'AUTO_PUBLISH', label: 'Publish' },
  { value: 'AUTO_CLEANUP', label: 'Cleanup' },
];

/**
 * CR161: Get chain preview text for a given source and checkpoint combination
 * Shows what steps will auto-run for the given configuration
 */
export function getChainPreview(
  sourcesEnabled: string[],
  checkpoint: ApprovalCheckpoint | null | undefined
): string {
  const cp = checkpoint || DEFAULT_CHECKPOINT;
  
  // FORUM-only case - MVP behavior
  if (sourcesEnabled.length === 1 && sourcesEnabled[0] === 'FORUM') {
    return 'FORUM will run: Trigger Community Signal Diagnostics (manual review)';
  }
  
  const collectionSteps: string[] = [];
  if (sourcesEnabled.includes('FLYER')) collectionSteps.push('S1 Flyer');
  if (sourcesEnabled.includes('WEB')) collectionSteps.push('S2 Web');
  
  if (collectionSteps.length === 0) {
    return 'No pipeline sources selected';
  }
  
  const processingSteps: Record<ApprovalCheckpoint, string[]> = {
    'STOP_AFTER_COLLECT': [],
    'STOP_AFTER_NORMALIZE_DEDUP': ['S4 Normalize', 'S5 Dedup', 'S5.5 Crop Snippets'],
    'STOP_AFTER_REVIEW': ['S4 Normalize', 'S5 Dedup', 'S5.5 Crop Snippets', 'S6 Review'],
    'AUTO_PUBLISH': ['S4 Normalize', 'S5 Dedup', 'S5.5 Crop Snippets', 'S6 Review', 'S7 Publish'],
    'AUTO_CLEANUP': ['S4 Normalize', 'S5 Dedup', 'S5.5 Crop Snippets', 'S6 Review', 'S7 Publish', 'S8 Cleanup'],
  };
  
  const allSteps = [...collectionSteps, ...processingSteps[cp]];
  const stepList = allSteps.join(' → ');
  const stopLabel = getRunUntilLabel(cp);
  
  return `This schedule will run: ${stepList} (then stop at ${stopLabel})`;
}
